import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { injectIntl } from 'react-intl';
import { createPaginationContainer, graphql } from 'react-relay';

import Loading from 'src/components/Loading';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';
import FlashesStore from 'src/stores/FlashesStore';
import {
  LIST_FILTER_ALL, LOAD_MORE, REFETCH,
} from 'src/util/constants';
import BrowserProtocol from 'src/util/history';
import logger from 'src/util/logger';
import { dateToTimeInTimezone, getTimezone } from 'src/util/time';

import AdminMeterTradeRulesTable from './AdminMeterTradeRulesTable';
import AdminTradeRulesControls from '../../AdminTradeRuleControls';

/**
 * This component displays trade rules for a specific meter in the admin interface.
 * It provides filtering capabilities by date, state, and type, and supports pagination
 * for viewing large sets of trade rules.
 * @param {object} props - Component props
 * @param {object} props.relay - Relay pagination container props
 * @param {object} props.meter - Meter data including identifier and trade rules
 * @param {object} props.match - React Router match object containing URL parameters
 * @param {object} props.variables - Query variables for fetching trade rules
 * @param {object} props.intl - Internationalization object from react-intl
 */
class AdminMeterTradeRules extends React.Component {
  constructor(props) {
    super(props);

    this.dateUpdateFunc = this.dateUpdateFunc.bind(this);
    this.filterFunc = this.filterFunc.bind(this);
    this.loadMore = this.loadMore.bind(this);

    const { variables, meter } = this.props;
    const { property } = meter || {};
    const { timezone: propertyTimezone } = property || {};
    const { state, type, start } = variables || {};

    const timezone = getTimezone(propertyTimezone);

    this.state = {
      date: start ? DateTime.fromSeconds(start, { zone: timezone }) : null,
      filter: {
        state: state || null,
        type: type || null,
      },
      updating: null,
    };
  }

  /**
   * Refetches trade rules data based on current filter and date settings
   * Updates URL query parameters to reflect current filter state
   */
  doRefetch = () => {
    const {
      relay, meter, match, intl,
    } = this.props;
    const { property } = meter || {};
    const { timezone: propertyTimezone } = property || {};

    const { uuid } = meter || {};
    const { location } = match || {};
    const { pathname } = location || {};

    const { date, filter } = this.state;
    const { state, type } = filter;

    const timezone = getTimezone(propertyTimezone);

    const variables = { uuid, state, type };
    if (date) {
      const timeInTimezone = dateToTimeInTimezone(date, timezone);
      variables.start = timeInTimezone.startOf('day').toSeconds();
      variables.finish = timeInTimezone.startOf('day').plus({ days: 1 }).toSeconds();
    }

    const queryParameters = new URLSearchParams();
    if (state) {
      queryParameters.append('state', state);
    }
    if (type) {
      queryParameters.append('type', type);
    }
    if (date) {
      queryParameters.append('date', date.toISODate({ zone: timezone }));
    }

    this.setState({ updating: REFETCH });

    const errorRefetchMessage = intl.formatMessage({
      id: 'admin.admin_meter.admin_meter_trade_rules.error.refetch',
      defaultMessage: 'Error refreshing trade rules data',
    });

    relay.refetchConnection(
      50,
      (error) => {
        if (error) {
          FlashesStore.flash(FlashesStore.ERROR, errorRefetchMessage);
        } else {
          BrowserProtocol.navigate({
            action: 'REPLACE',
            pathname,
            search: queryParameters.toString() ? `?${queryParameters.toString()}` : '',
            hash: '',
          });
        }
        this.setState({ updating: null });
      },
      variables,
    );
  };

  /**
   * Updates the selected date and triggers a data refetch
   * @param {DateTime} date - The selected date
   */
  dateUpdateFunc = (date) => {
    const { updating } = this.state;

    if (updating) {
      return;
    }

    this.setState({ date }, this.doRefetch);
  };

  /**
   * Updates the filter settings and triggers a data refetch
   * @param {string} key - The filter key to update (state or type)
   * @param {string} value - The new filter value
   */
  filterFunc = (key, value) => {
    const { filter, updating } = this.state;
    const { state, type } = filter;

    if (updating) {
      return;
    }

    const newFilter = { state, type };
    const newState = { filter: newFilter };

    if (value === LIST_FILTER_ALL) {
      newFilter.state = null;
      newFilter.type = null;
      newState.date = null;
    } else if (filter[key] === value) {
      newFilter[key] = null;
    } else {
      newFilter[key] = value;
    }

    this.setState(newState, this.doRefetch);
  };

  /**
   * Loads more trade rules data when scrolling through the list
   */
  loadMore = () => {
    const { relay, intl } = this.props;
    const { updating } = this.state;

    if (!relay.hasMore() || relay.isLoading()) {
      return;
    }

    if (updating) {
      return;
    }

    this.setState({ updating: LOAD_MORE });

    const errorLoadMoreMessage = intl.formatMessage({
      id: 'admin.admin_meter.admin_meter_trade_rules.error.load_more',
      defaultMessage: 'Error loading more trade rules',
    });

    relay.loadMore(
      50,
      (error) => {
        if (error) {
          logger.log(error);
          FlashesStore.flash(FlashesStore.ERROR, errorLoadMoreMessage);
        }
        this.setState({ updating: null });
      },
    );
  };

  render() {
    if (!this.props) {
      return <Loading />;
    }

    const { relay, meter, intl } = this.props;
    const {
      uuid, identifier, rules, title,
    } = meter || {};

    const { date, filter, updating } = this.state;

    // Page title and headings
    const pageTitle = intl.formatMessage(
      {
        id: 'admin.admin_meter.admin_meter_trade_rules.page.title',
        defaultMessage: 'Enosi Admin - Meter - {identifier} - Trade Rules',
      },
      { identifier },
    );
    const headingContext = intl.formatMessage({
      id: 'admin.admin_meter.admin_meter_trade_rules.heading.context',
      defaultMessage: 'Trade Rules',
    });

    // Breadcrumb labels
    const breadcrumbAdmin = intl.formatMessage({
      id: 'admin.admin_meter.admin_meter_trade_rules.breadcrumb.admin',
      defaultMessage: 'Admin',
    });
    const breadcrumbMeters = intl.formatMessage({
      id: 'admin.admin_meter.admin_meter_trade_rules.breadcrumb.meters',
      defaultMessage: 'Meters',
    });
    const breadcrumbMeter = intl.formatMessage(
      {
        id: 'admin.admin_meter.admin_meter_trade_rules.breadcrumb.meter',
        defaultMessage: '{title} ({identifier})',
      },
      { identifier, title },
    );
    const breadcrumbTradeRules = intl.formatMessage({
      id: 'admin.admin_meter.admin_meter_trade_rules.breadcrumb.trade_rules',
      defaultMessage: 'Trade Rules',
    });

    return (
      <>
        <Helmet>
          <meta charSet="utf-8" />
          <title>{pageTitle}</title>
        </Helmet>

        <Breadcrumbs
          breadcrumbs={[
            { name: breadcrumbAdmin, path: '/admin' },
            { name: breadcrumbMeters, path: '/admin/meters' },
            { name: breadcrumbMeter, path: `/admin/meters/${uuid}` },
            { name: breadcrumbTradeRules },
          ]}
        />

        <Heading
          title={identifier}
          context={headingContext}
        />

        <AdminTradeRulesControls
          date={date}
          dateUpdateFunc={this.dateUpdateFunc}
          filter={filter}
          filterFunc={this.filterFunc}
        />

        <AdminMeterTradeRulesTable
          meter={meter}
          rules={rules}
          moreFunc={this.loadMore}
          hasMore={relay.hasMore()}
          updating={updating}
        />
      </>
    );
  }
}

AdminMeterTradeRules.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
    refetchConnection: PropTypes.func,
  }).isRequired,
  meter: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  match: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  variables: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  intl: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

AdminMeterTradeRules.defaultProps = {
  meter: null,
};

export default injectIntl(createPaginationContainer(
  AdminMeterTradeRules,
  {
    meter: graphql`
      fragment AdminMeterTradeRules_meter on Meter {
        uuid
        identifier
        title
        tradePointId
        property {
          uuid
          timezone
        }
        rules(
          first: $count
          after: $cursor
          state: $state
          type: $type
          start: $start
          finish: $finish
        ) @connection(key: "AdminMeterTradeRules_rules") {
          edges {
            cursor
            node {
              uuid
            }
          }
          ...AdminMeterTradeRulesTable_rules
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps(props) {
      return props.meter.rules;
    },
    // This is also the default implementation of `getFragmentVariables` if it isn't provided.
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }, fragmentVariables) {
      const variables = {
        count, cursor, state: null, type: null, start: null, finish: null,
      };

      // Set if present
      const {
        uuid, state, type, start, finish,
      } = fragmentVariables;
      if (uuid !== undefined && uuid !== null) { variables.uuid = uuid; }
      if (state !== undefined && state !== null) { variables.state = state; }
      if (type !== undefined && type !== null) { variables.type = type; }
      if (start !== undefined && start !== null) { variables.start = start; }
      if (finish !== undefined && finish !== null) { variables.finish = finish; }

      return variables;
    },
    query: graphql`
      # Pagination query to be fetched upon calling 'loadMore'.
      # Notice that we re-use our fragment, and the shape of this query matches our fragment spec.
      query AdminMeterTradeRules_Pagination_Query(
        $uuid: ID!
        $count: Int!
        $cursor: String
        $state: TradeRuleState
        $type: TradeType
        $start: Timestamp
        $finish: Timestamp
      ) {
        meter(uuid: $uuid) {
          ...AdminMeterTradeRules_meter
        }
      }
    `,
  },
));
